import { noop } from 'underscore';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { useIntersectionObserver, extractWebsite } from '@bingads-webui-campaign-react/pmax-landing-utils';

import { CouponBanner } from '../shared';
import { useLoadSuggestions } from './use-load-suggestions';
import { useDynamicFlowState, getIsPreviewState } from './use-dynamic-flow-state';
import { DiscoverSection } from './discover-section';
import { PreviewSection } from './preview-section';
import { getDynamicAdCoverStyles } from '../styles/dynamic-ad-cover-styles';

export function DynamicAdCover({
  onClickSignUp,
  onClickClaimCoupon,
  signUpUrlWithCacheId,
  setCacheId,
  setShowAdPreviewPlaceholder,
  websiteInputRef,
  methodsContainerRef,
  logger,
}) {
  const classes = useDefaultStyles(getDynamicAdCoverStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const ref = useRef();

  const onScrollToView = useCallback(() => {
    logger.trace('DynamicAdCover scrolled into view');
  }, [logger]);

  useIntersectionObserver({
    ref,
    onScrollToView,
    options: { threshold: 0.2 },
  });

  const websiteFromUrl = extractWebsite();
  const [website, setWebsite] = useState(websiteFromUrl);
  const [prompt, setPrompt] = useState('');

  const {
    currentState,
    triggerTransition,
  } = useDynamicFlowState(websiteFromUrl);

  const {
    promptSuggestions,
    adSuggestions,
    errorMessage,
  } = useLoadSuggestions({
    currentState,
    triggerTransition,
    website,
    prompt,
    setCacheId,
    logger,
  });

  const isPreviewState = getIsPreviewState(currentState);

  useEffect(() => {
    setShowAdPreviewPlaceholder(!isPreviewState);
  }, [isPreviewState, setShowAdPreviewPlaceholder]);

  return (
    <div ref={ref} className={classNames('dynamic-ad-cover-container', classes.container, classes[`container-${displayMode}`])}>
      <CouponBanner logger={logger} onClickClaimCoupon={onClickClaimCoupon} />
      {!isPreviewState ?
        <DiscoverSection
          currentState={currentState}
          triggerTransition={triggerTransition}
          onClickSignUp={onClickSignUp}
          website={website}
          setWebsite={setWebsite}
          websiteInputRef={websiteInputRef}
          methodsContainerRef={methodsContainerRef}
          logger={logger}
        /> :
        <PreviewSection
          currentState={currentState}
          triggerTransition={triggerTransition}
          promptSuggestions={promptSuggestions}
          adSuggestions={adSuggestions}
          onClickSignUp={onClickSignUp}
          website={website}
          setWebsite={setWebsite}
          prompt={prompt}
          setPrompt={setPrompt}
          signUpUrlWithCacheId={signUpUrlWithCacheId}
          errorMessage={errorMessage}
          logger={logger}
        />
      }
    </div>
  );
}

DynamicAdCover.propTypes = {
  onClickSignUp: PropTypes.func.isRequired,
  onClickClaimCoupon: PropTypes.func.isRequired,
  signUpUrlWithCacheId: PropTypes.string.isRequired,
  setCacheId: PropTypes.func.isRequired,
  setShowAdPreviewPlaceholder: PropTypes.func,
  websiteInputRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  methodsContainerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
};

DynamicAdCover.defaultProps = {
  setShowAdPreviewPlaceholder: noop,
  websiteInputRef: { current: null },
  methodsContainerRef: { current: null },
  logger: {
    trace: noop,
  },
};
